// src/pages/AdendaBitacora.js
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Container,
  Typography,
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Description as XmlIcon,
  TableChart as ExcelIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, FieldArray, Field } from "formik";
import * as Yup from "yup";
import * as XLSX from 'xlsx';
import FormikTextField from "../components/FormikTextField";
import ConceptoAutocomplete from "../components/ConceptoAutocomplete";
import BitacoraAutocomplete from "../components/BitacoraAutocomplete";
import { useAuth } from "../context/AuthContext";
import { useSftfApi } from "../api/sftfClient";
import { useSidebar } from "../context/SidebarContext";
import { getCurrentEnvironment } from "../config/environment";
import { Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material";
import { pdfFile, xmlFile } from "./files";
import { useExcelImport } from "../utils/useExcelImport";
import { integrarAddendaEnFactura, generarAddendaStandalone } from "../utils/addendaXml";
import ExcelBlockSelectorDialog from "../components/excel/ExcelBlockSelectorDialog";
import ApoyoSelectorDialog from "../components/excel/ApoyoSelectorDialog";
import * as pdfjsLib from "pdfjs-dist";

// Configurar el worker de PDF.js para extraer texto localmente (archivo copiado a public/)
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export default function AdendaBitacora() {
  const { auth } = useAuth();
  const { consultarTiposFactura, consultarEstatusFactura, buscarBitacoras } = useSftfApi(auth);
  const { collapsed } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [xmlPreview, setXmlPreview] = useState("");
  const [previewValues, setPreviewValues] = useState(null);
  const [successDialog, setSuccessDialog] = useState(false);
  const [errorDialog, setErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tiposFactura, setTiposFactura] = useState([]);
  const [estatusFactura, setEstatusFactura] = useState([]);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);
  const [archivosFactura, setArchivosFactura] = useState([]);
  const [xmlFactura, setXmlFactura] = useState(null);
  const [showFormatted, setShowFormatted] = useState(true);
  const [totalFactura, setTotalFactura] = useState(0);
  const [subtotalFactura, setSubtotalFactura] = useState(0);
  const [conceptoSearchTerm, setConceptoSearchTerm] = useState("");
  const [cargandoExcel, setCargandoExcel] = useState(false);
  const [impuestosFactura, setImpuestosFactura] = useState({ total: 0, traslados: 0, retenciones: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const { parseExcelFile, extraeBloqueEspecifico, mapearBitacorasAConceptos } = useExcelImport();

  // Estados para manejar el diálogo de selección de tablas del Excel
  const [excelSheetsData, setExcelSheetsData] = useState([]);
  const [selectedSheetName, setSelectedSheetName] = useState("");
  const [showBlockSelector, setShowBlockSelector] = useState(false);
  const activeFormRef = useRef(null);

  // Estados para manejar el diálogo de selección de apoyo si existen múltiples
  const [apoyosDisponibles, setApoyosDisponibles] = useState([]);
  const [bitacorasPendientesPorApoyo, setBitacorasPendientesPorApoyo] = useState([]);
  const [showApoyoSelector, setShowApoyoSelector] = useState(false);

  // Refs para los campos de importe de los conceptos
  const conceptoImporteRef = useRef(null);

  // Computed vars based on selected sheet
  const activeSheet = excelSheetsData.find(s => s.sheetName === selectedSheetName);
  const excelRawData = activeSheet ? activeSheet.rawData : [];
  const excelBloques = activeSheet ? activeSheet.bloquesEncontrados : [];

  // Obtener datos de la bitácora seleccionada desde el estado de navegación
  const bitacoraData = location.state?.bitacoraData || {};

  // Funciones de cálculo y validación
  const calcularSumaDetalles = (detalles) => {
    return detalles.reduce((suma, detalle) => suma + (parseFloat(detalle.importe) || 0), 0);
  };

  const calcularSumaBitacoras = (bitacorasViaje) => {
    return bitacorasViaje.reduce((suma, bitacora) => suma + (parseFloat(bitacora.importe) || 0), 0);
  };


  const validarSumaDetalles = (detalles, importeBitacora) => {
    const sumaDetalles = calcularSumaDetalles(detalles);
    const importeBitacoraNum = parseFloat(importeBitacora) || 0;

    // Si ambos son 0, no es válido (debe haber al menos un valor)
    if (sumaDetalles === 0 && importeBitacoraNum === 0) {
      return false;
    }

    return Math.abs(sumaDetalles - importeBitacoraNum) < 0.01;
  };

  const validarSumaBitacoras = (bitacorasViaje, importeConcepto) => {
    const sumaBitacoras = calcularSumaBitacoras(bitacorasViaje);
    const importeConceptoNum = parseFloat(importeConcepto) || 0;

    // Si ambos son 0, no es válido (debe haber al menos un valor)
    if (sumaBitacoras === 0 && importeConceptoNum === 0) {
      return false;
    }

    return Math.abs(sumaBitacoras - importeConceptoNum) < 0.01;
  };

  // Función para hacer la llamada directamente con fetch (sin axios)
  const postActualizaDirect = async (facturas) => {
    const env = getCurrentEnvironment();
    const baseUrl = env.isPreprod
      ? "https://servicios.cryoinfra.com.mx/wsk_sftf/Mensaje/CryoDAC-SFTF/SFTF/FacturaFletera/Actualizar"
      : "https://servicios.cryoinfra.com.mx/hub_sdl_01/Mensaje/CryoDAC_SDLSFTF_PRO/SDLSFTF_PRO/FacturaFletera/Actualizar";

    const payload = {
      user: auth.user,
      pass: auth.pass,
      facturas: facturas
    };

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify(payload);

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    console.log("=== POST ACTUALIZA DIRECT (FETCH) ===");
    console.log("Environment:", env.name);
    console.log("URL:", baseUrl);
    console.log("Payload:", raw);
    console.log("Payload size:", raw.length, "bytes");

    const response = await fetch(baseUrl, requestOptions);

    console.log("Response status:", response.status);
    console.log("Response statusText:", response.statusText);
    console.log("Response headers:", response.headers);
    console.log("Response Content-Type:", response.headers.get("content-type"));

    const text = await response.text();
    console.log("Response text (first 1000 chars):", text.substring(0, 1000));
    console.log("Response text (full):", text);

    // Intentar parsear como JSON
    let jsonData;
    try {
      jsonData = JSON.parse(text);
      console.log("Response JSON:", jsonData);
    } catch (e) {
      console.log("No es JSON válido, es texto plano o HTML");
      // Si no es JSON, lanzar error con el texto
      const error = new Error(text.includes('Not Found') ? 'Not Found' : text);
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: {
          message: text.includes('Not Found') ? 'Not Found' : text,
          Success: false,
          Message: text.includes('Not Found') ? 'Not Found' : text
        }
      };
      throw error;
    }

    // Si success es false, lanzar error para manejo consistente
    if (jsonData && jsonData.success === false) {
      const error = new Error(jsonData.message || 'Error al procesar la solicitud');
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: jsonData
      };
      throw error;
    }

    return jsonData;
  };

  // Función de debug para probar la API directamente
  const handleDebugFetch = async () => {
    console.log("=== DEBUG FETCH INICIADO ===");
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "user": "LTA2308086X4",
      "pass": "16dd338e66ccdb36bdf89a843428bb3eb3cbca3be49063d693867ae7c1db6dda",
      "facturas": [
        {
          "ClaveTipoFactura": 2,
          "RemitenteRFC": "LTA2308086X4",
          "Estatus": 1,
          "attachPathPDF": pdfFile,
          "attachPathXML": xmlFile
        }
      ]
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    try {
      console.log("URL:", "https://servicios.cryoinfra.com.mx/wsk_sftf/Mensaje/CryoDAC-SFTF/SFTF/FacturaFletera/Actualizar");
      console.log("Payload:", raw);

      const response = await fetch(
        "https://servicios.cryoinfra.com.mx/wsk_sftf/Mensaje/CryoDAC-SFTF/SFTF/FacturaFletera/Actualizar",
        requestOptions
      );

      console.log("Response status:", response.status);
      console.log("Response statusText:", response.statusText);
      console.log("Response headers:", response.headers);
      console.log("Response Content-Type:", response.headers.get("content-type"));

      const text = await response.text();
      console.log("Response text (first 1000 chars):", text.substring(0, 1000));
      console.log("Response text (full):", text);

      // Intentar parsear como JSON
      try {
        const json = JSON.parse(text);
        console.log("Response JSON:", json);
        alert(`Respuesta exitosa:\n${JSON.stringify(json, null, 2)}`);
      } catch (e) {
        console.log("No es JSON válido, es texto plano o HTML");
        alert(`Respuesta (texto):\n${text.substring(0, 500)}${text.length > 500 ? '...' : ''}`);
      }
    } catch (error) {
      console.error("Error en fetch:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const extraerTotalFactura = (xmlString) => {
    try {
      // Validar que el XML no esté vacío
      if (!xmlString || typeof xmlString !== 'string' || xmlString.trim() === '') {
        console.warn('XML vacío o inválido en extraerTotalFactura');
        return { total: 0, subtotal: 0 };
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

      // Verificar errores de parsing
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        console.error('Error al parsear XML en extraerTotalFactura:', parserError.textContent);
        return { total: 0, subtotal: 0 };
      }

      // Verificar que el documento tenga un elemento raíz
      if (!xmlDoc.documentElement) {
        console.warn('XML no tiene elemento raíz en extraerTotalFactura');
        return { total: 0, subtotal: 0 };
      }

      // Buscar los atributos Total y SubTotal en el elemento Comprobante
      const comprobante = xmlDoc.querySelector('cfdi\\:Comprobante, Comprobante');
      if (comprobante) {
        const total = comprobante.getAttribute('Total');
        const subtotal = comprobante.getAttribute('SubTotal');
        return {
          total: total ? parseFloat(total) : 0,
          subtotal: subtotal ? parseFloat(subtotal) : 0
        };
      }

      return { total: 0, subtotal: 0 };
    } catch (error) {
      console.error('Error al extraer total de la factura:', error);
      return { total: 0, subtotal: 0 };
    }
  };

  // Función para extraer mercancía de la Carta Porte
  const extraerMercanciaCartaPorte = (xmlContent) => {
    let textoBusqueda = "";
    try {
      const parser = new DOMParser();
      const localDoc = parser.parseFromString(xmlContent, 'text/xml');
      
      // Buscar ignorando el namespace específico (cartaporte31, etc.)
      const allElements = Array.from(localDoc.getElementsByTagName('*'));
      const mercanciaNode = allElements.find(node => node.localName === 'Mercancia');
      
      if (mercanciaNode) {
        textoBusqueda = 
          mercanciaNode.getAttribute('Descripcion') || 
          mercanciaNode.getAttribute('descripcion') || 
          mercanciaNode.getAttribute('BienesTransp') || 
          "";
      }
    } catch (error) {
      console.warn("Fallo al buscar el nodo Mercancia dentro del XML:", error);
    }
    
    return textoBusqueda;
  };

  // Función para extraer conceptos del XML de la factura
  const extraerConceptosFactura = (xmlString) => {
    try {
      // Validar que el XML no esté vacío
      if (!xmlString || typeof xmlString !== 'string' || xmlString.trim() === '') {
        console.warn('XML vacío o inválido en extraerConceptosFactura');
        return [];
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

      // Verificar errores de parsing
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        console.error('Error al parsear XML en extraerConceptosFactura:', parserError.textContent);
        return [];
      }

      // Verificar que el documento tenga un elemento raíz
      if (!xmlDoc.documentElement) {
        console.warn('XML no tiene elemento raíz en extraerConceptosFactura');
        return [];
      }

      const conceptos = [];

      // Buscar todos los conceptos en el XML (pueden estar en diferentes namespaces)
      const conceptosNodes = xmlDoc.querySelectorAll('cfdi\\:Concepto, Concepto, *[local-name()="Concepto"]');

      conceptosNodes.forEach(conceptoNode => {
        const importe = conceptoNode.getAttribute('Importe');
        const descripcion = conceptoNode.getAttribute('Descripcion') || conceptoNode.getAttribute('descripcion') || '';
        const cantidad = conceptoNode.getAttribute('Cantidad') || conceptoNode.getAttribute('cantidad') || '';
        const valorUnitario = conceptoNode.getAttribute('ValorUnitario') || conceptoNode.getAttribute('valorUnitario') || '';

        // Buscar impuestos dentro del concepto
        const impuestosNode = conceptoNode.querySelector('cfdi\\:Impuestos, Impuestos, *[local-name()="Impuestos"]');
        let importeImpuesto = 0;

        if (impuestosNode) {
          // Buscar traslados (IVA, etc.)
          const trasladosNode = impuestosNode.querySelector('cfdi\\:Traslados, Traslados, *[local-name()="Traslados"]');
          if (trasladosNode) {
            const trasladoNodes = trasladosNode.querySelectorAll('cfdi\\:Traslado, Traslado, *[local-name()="Traslado"]');
            trasladoNodes.forEach(traslado => {
              const importeTraslado = traslado.getAttribute('Importe') || traslado.getAttribute('importe') || '0';
              importeImpuesto += parseFloat(importeTraslado) || 0;
            });
          }

          // Buscar retenciones
          const retencionesNode = impuestosNode.querySelector('cfdi\\:Retenciones, Retenciones, *[local-name()="Retenciones"]');
          if (retencionesNode) {
            const retencionNodes = retencionesNode.querySelectorAll('cfdi\\:Retencion, Retencion, *[local-name()="Retencion"]');
            retencionNodes.forEach(retencion => {
              const importeRetencion = retencion.getAttribute('Importe') || retencion.getAttribute('importe') || '0';
              importeImpuesto += parseFloat(importeRetencion) || 0;
            });
          }
        }

        if (importe) {
          conceptos.push({
            importe: parseFloat(importe) || 0,
            descripcion: descripcion,
            cantidad: cantidad,
            valorUnitario: valorUnitario,
            importeImpuesto: importeImpuesto
          });
        }
      });

      return conceptos;
    } catch (error) {
      console.error('Error al extraer conceptos de la factura:', error);
      return [];
    }
  };

  // Función para extraer impuestos totales del XML de la factura
  const extraerImpuestosFactura = (xmlString) => {
    try {
      // Validar que el XML no esté vacío
      if (!xmlString || typeof xmlString !== 'string' || xmlString.trim() === '') {
        console.warn('XML vacío o inválido en extraerImpuestosFactura');
        return { total: 0, traslados: 0, retenciones: 0 };
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

      // Verificar errores de parsing
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        console.error('Error al parsear XML en extraerImpuestosFactura:', parserError.textContent);
        return { total: 0, traslados: 0, retenciones: 0 };
      }

      // Verificar que el documento tenga un elemento raíz
      if (!xmlDoc.documentElement) {
        console.warn('XML no tiene elemento raíz en extraerImpuestosFactura');
        return { total: 0, traslados: 0, retenciones: 0 };
      }

      // Buscar el nodo de impuestos en el comprobante
      const comprobante = xmlDoc.querySelector('cfdi\\:Comprobante, Comprobante');
      if (!comprobante) return { total: 0, traslados: 0, retenciones: 0 };

      const impuestosNode = comprobante.querySelector('cfdi\\:Impuestos, Impuestos, *[local-name()="Impuestos"]');
      if (!impuestosNode) return { total: 0, traslados: 0, retenciones: 0 };

      let totalTraslados = 0;
      let totalRetenciones = 0;

      // Buscar traslados (IVA, etc.)
      const trasladosNode = impuestosNode.querySelector('cfdi\\:Traslados, Traslados, *[local-name()="Traslados"]');
      if (trasladosNode) {
        const trasladoNodes = trasladosNode.querySelectorAll('cfdi\\:Traslado, Traslado, *[local-name()="Traslado"]');
        trasladoNodes.forEach(traslado => {
          const importe = traslado.getAttribute('Importe') || traslado.getAttribute('importe') || '0';
          totalTraslados += parseFloat(importe) || 0;
        });
      }

      // Buscar retenciones
      const retencionesNode = impuestosNode.querySelector('cfdi\\:Retenciones, Retenciones, *[local-name()="Retenciones"]');
      if (retencionesNode) {
        const retencionNodes = retencionesNode.querySelectorAll('cfdi\\:Retencion, Retencion, *[local-name()="Retencion"]');
        retencionNodes.forEach(retencion => {
          const importe = retencion.getAttribute('Importe') || retencion.getAttribute('importe') || '0';
          totalRetenciones += parseFloat(importe) || 0;
        });
      }

      return {
        total: totalTraslados - totalRetenciones,
        traslados: totalTraslados,
        retenciones: totalRetenciones
      };
    } catch (error) {
      console.error('Error al extraer impuestos de la factura:', error);
      return { total: 0, traslados: 0, retenciones: 0 };
    }
  };

  const validarSumaConceptos = (conceptos, subtotalFactura) => {
    const sumaConceptos = conceptos.reduce((suma, concepto) => suma + (parseFloat(concepto.importe) || 0), 0);

    // Si no hay factura cargada, solo validar que haya al menos un concepto con importe > 0
    if (subtotalFactura === 0) {
      return sumaConceptos > 0;
    }

    return Math.abs(sumaConceptos - subtotalFactura) < 0.01;
  };

  // Cargar catálogos al montar el componente
  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        setCargandoCatalogos(true);

        // Ambos catálogos se piden en paralelo: antes se esperaban en
        // secuencia y, con un hub lento, cada uno podía tardar hasta ~93 s
        // (3 intentos, corte a 45 s), dejando el formulario bloqueado casi
        // 3 minutos en el peor caso.
        const [tiposData, estatusData] = await Promise.all([
          consultarTiposFactura(),
          consultarEstatusFactura(),
        ]);

        if (tiposData && Array.isArray(tiposData)) {
          setTiposFactura(tiposData);
        }
        if (estatusData && Array.isArray(estatusData)) {
          setEstatusFactura(estatusData);
        }
      } catch (err) {
        console.error("Error al cargar catálogos:", err);
        setError("Error al cargar los catálogos de tipos de factura y estatus");
      } finally {
        setCargandoCatalogos(false);
      }
    };

    cargarCatalogos();
  }, [consultarTiposFactura, consultarEstatusFactura]); // Incluir dependencias

  const validationSchema = Yup.object({
    claveTipoFactura: Yup.string().required("El tipo de factura es requerido"),
    estatus: Yup.string().required("El estatus es requerido"),
    conceptos: Yup.array().of(
      Yup.object({
        clave: Yup.object().required("La clave es requerida"),
        importe: Yup.number().required("El importe es requerido").min(0, "El importe debe ser mayor a 0"),
        bitacorasViaje: Yup.array().of(
          Yup.object({
            claveBitacora: Yup.mixed()
              .required("La clave de bitácora es requerida")
              .test('claveBitacora-valid', 'La clave de bitácora debe ser un objeto o un string/número válido', function (value) {
                // Aceptar objetos, strings no vacíos, o números
                if (!value) return false;
                if (typeof value === 'object') return true;
                if (typeof value === 'string' && value.trim() !== '') return true;
                if (typeof value === 'number') return true;
                return false;
              }),
            fecha: Yup.date().required("La fecha es requerida"),
            importe: Yup.number().required("El importe es requerido").min(0, "El importe debe ser mayor a 0"),
            detalles: Yup.array().of(
              Yup.object({
                descripcion: Yup.string().required("La descripción es requerida"),
                fecha: Yup.date().required("La fecha es requerida"),
                importe: Yup.number().required("El importe es requerido").min(0, "El importe debe ser mayor a 0")
              })
            )
          })
        )
      })
    )
  }).test('validar-sumas', 'Las sumas no coinciden', function (values) {
    if (!values.conceptos) return true;

    // Validar que la suma de conceptos sea igual al total de la factura (si hay factura cargada)
    if (subtotalFactura > 0) {
      const sumaConceptos = values.conceptos.reduce((suma, concepto) => suma + (parseFloat(concepto.importe) || 0), 0);
      if (Math.abs(sumaConceptos - subtotalFactura) >= 0.01) {
        return this.createError({
          path: 'conceptos',
          message: `La suma de conceptos (${sumaConceptos.toFixed(2)}) debe ser igual al subtotal de la factura (${subtotalFactura.toFixed(2)})`
        });
      }
    }

    for (const concepto of values.conceptos) {
      // Validar que la suma de bitácoras sea igual al importe del concepto
      const sumaBitacoras = calcularSumaBitacoras(concepto.bitacorasViaje || []);
      if (Math.abs(sumaBitacoras - (parseFloat(concepto.importe) || 0)) >= 0.01) {
        return this.createError({
          path: 'conceptos',
          message: `La suma de bitácoras (${sumaBitacoras.toFixed(2)}) debe ser igual al importe del concepto (${concepto.importe})`
        });
      }

      // Validar que la suma de detalles sea igual al importe de cada bitácora
      for (const bitacora of concepto.bitacorasViaje || []) {
        const sumaDetalles = calcularSumaDetalles(bitacora.detalles || []);
        if (Math.abs(sumaDetalles - (parseFloat(bitacora.importe) || 0)) >= 0.01) {
          return this.createError({
            path: 'conceptos',
            message: `La suma de detalles (${sumaDetalles.toFixed(2)}) debe ser igual al importe de la bitácora (${bitacora.importe})`
          });
        }
      }
    }

    return true;
  });

  // Función para obtener valores iniciales con defaults
  const getInitialValues = () => {
    // Buscar "Arrastre" en tipos de factura (ClaveTipoFactura: 1)
    const tipoArrastre = tiposFactura.find(t =>
      t.ClaveTipoFactura === 1 ||
      t.Descripcion?.toLowerCase().includes('arrastre')
    );
    const defaultTipoFactura = tipoArrastre ? tipoArrastre.ClaveTipoFactura.toString() : "";

    // Buscar "Procesada" en estatus (ClaveEstatusFactura: 1)
    const estatusProcesada = estatusFactura.find(e =>
      e.ClaveEstatusFactura === 1 ||
      e.EstatusFactura?.toLowerCase().includes('procesada')
    );
    const defaultEstatus = estatusProcesada ? estatusProcesada.ClaveEstatusFactura.toString() : "";

    return {
      claveTipoFactura: defaultTipoFactura,
      estatus: defaultEstatus,
      conceptos: [
        {
          clave: null,
          importe: 0,
          bitacorasViaje: [
            {
              claveBitacora: bitacoraData.claveBitacora ? {
                claveBitacora: bitacoraData.claveBitacora,
                serieBitacora: bitacoraData.serieBitacora || "",
                folioBitacora: bitacoraData.folioBitacora || "",
                origenNombre: bitacoraData.origenNombre || "",
                destinoNombre: bitacoraData.destinoNombre || "",
                operadorNombre: bitacoraData.operadorNombre || ""
              } : null,
              fecha: new Date().toISOString().split('T')[0],
              importe: 0,
              detalles: [
                {
                  descripcion: "",
                  fecha: new Date().toISOString().split('T')[0],
                  importe: 0
                }
              ]
            }
          ]
        }
      ]
    };
  };

  const initialValues = getInitialValues();

  // Función para manejar la carga de archivos
  const handleFileUpload = (event, setFieldValue) => {
    const files = Array.from(event.target.files);
    // Resetear el input para permitir volver a seleccionar el mismo archivo
    event.target.value = '';

    // Validar que solo sean XML o PDF
    const archivosAceptados = [];
    for (const file of files) {
      const isXMLFile = file.type === 'text/xml' || file.type === 'application/xml' || file.name.toLowerCase().endsWith('.xml');
      const isPDFFile = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

      if (!isXMLFile && !isPDFFile) {
        setErrorMessage(`El archivo "${file.name}" no es válido. Solo se aceptan archivos XML y PDF.`);
        setErrorDialog(true);
        continue;
      }
      archivosAceptados.push(file);
    }

    if (archivosAceptados.length === 0) return;

    // Validar duplicados contra el estado actual
    const yaExisteXML = archivosFactura.some(f => f.type === 'text/xml' || f.type === 'application/xml' || f.name.toLowerCase().endsWith('.xml'));
    const yaExistePDF = archivosFactura.some(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));

    const archivosPermitidos = [];
    for (const file of archivosAceptados) {
      const isXMLFile = file.type === 'text/xml' || file.type === 'application/xml' || file.name.toLowerCase().endsWith('.xml');
      const isPDFFile = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

      if (isXMLFile && yaExisteXML) {
        setErrorMessage('Ya existe un archivo XML cargado. Elimínalo antes de subir uno nuevo.');
        setErrorDialog(true);
        continue;
      }
      if (isPDFFile && yaExistePDF) {
        setErrorMessage('Ya existe un archivo PDF cargado. Elimínalo antes de subir uno nuevo.');
        setErrorDialog(true);
        continue;
      }
      archivosPermitidos.push(file);
    }

    if (archivosPermitidos.length === 0) return;

    setArchivosFactura(prev => [
      ...prev,
      ...archivosPermitidos.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadDate: new Date().toISOString()
      }))
    ]);

    // Procesar archivos XML/PDF aceptados
    archivosPermitidos.forEach(file => {
      // Solo procesar archivos XML, no PDFs u otros tipos
      const isXML = file.type === 'text/xml' ||
        file.type === 'application/xml' ||
        file.name.toLowerCase().endsWith('.xml');

      const isPDF = file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf');

      if (isXML) {
        console.log('Procesando archivo XML:', file.name);
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const xmlContent = e.target.result;

            // Validar que el contenido no esté vacío
            if (!xmlContent || xmlContent.trim() === '') {
              console.error('El archivo XML está vacío');
              setError('El archivo XML está vacío o no se pudo leer correctamente');
              return;
            }

            // Validar que parezca XML (debe empezar con < o <?xml)
            if (!xmlContent.trim().startsWith('<')) {
              console.error('El archivo no parece ser un XML válido (no empieza con <)');
              setError('El archivo no es un XML válido');
              return;
            }

            setXmlFactura(xmlContent);

            // Extraer el total de la factura
            const { total, subtotal } = extraerTotalFactura(xmlContent);
            setTotalFactura(total);
            setSubtotalFactura(subtotal);

            // Extraer impuestos de la factura
            const impuestos = extraerImpuestosFactura(xmlContent);
            setImpuestosFactura(impuestos);

            console.log('✅ XML de factura cargado exitosamente');
            console.log('Total de la factura extraído:', total);
            console.log('SubTotal de la factura extraído:', subtotal);
            console.log('Impuestos de la factura extraídos:', impuestos);

            // Intentar extraer término principal de búsqueda desde la Carta Porte
            const textoBusqueda = extraerMercanciaCartaPorte(xmlContent);
            if (textoBusqueda) {
              setConceptoSearchTerm(textoBusqueda);
              console.log('Concepto search term:', textoBusqueda);
            }
            
            // Rellenar los campos de importe del Concepto 1, Bitácora 1 y Detalle 1 con el subtotal
            if (setFieldValue) {
              setFieldValue("conceptos.0.importe", subtotal);
              setFieldValue("conceptos.0.bitacorasViaje.0.importe", subtotal);
              setFieldValue("conceptos.0.bitacorasViaje.0.detalles.0.importe", subtotal);
            }
            
            // Backup direct ref update just in case
            if (conceptoImporteRef.current) {
              conceptoImporteRef.current.value = subtotal;
            }

          } catch (error) {
            console.error('Error al procesar XML:', error);
            setError('Error al procesar el archivo XML: ' + error.message);
          }
        };
        reader.onerror = (error) => {
          console.error('Error al leer el archivo XML:', error);
          setError('Error al leer el archivo XML');
        };
        reader.readAsText(file);
      } else if (isPDF) {
        // Procesar PDF inmediatamente al cargar
        console.log('=== Procesando PDF al cargar ===');
        console.log('Archivo PDF:', file.name);
        console.log('Tamaño del archivo:', file.size, 'bytes');

        // Parsear PDF texto para encontrar mercancía (Gas LIN, CO2, etc)
        const pdfReader = new FileReader();
        pdfReader.onload = async (ev) => {
          try {
            const arrayBuffer = ev.target.result;
            const pdfData = new Uint8Array(arrayBuffer);
            const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
            let fullText = "";
            for (let i = 1; i <= pdf.numPages; i++) {
              const page = await pdf.getPage(i);
              const textContent = await page.getTextContent();
              const pageText = textContent.items.map(item => item.str).join(" ");
              fullText += pageText + " ";
            }
            console.log("PDF Extracted Text (primeros 500 chars):", fullText.substring(0, 500));
            // Actualizar el conceptoSearchTerm y disparar el filtrado de autocompletado del dropdown
            if (fullText) {
              // Usar un pequeño retraso para asegurar que el state se asiente
              setTimeout(() => {
                setConceptoSearchTerm(fullText);
              }, 100);
            }
          } catch (pdfError) {
            console.error("Error al extraer texto del PDF:", pdfError);
          }
        };
        pdfReader.readAsArrayBuffer(file);

        fileToBase64(file).then(base64 => {
          console.log('=== BASE64 DEL PDF (al cargar) ===');
          console.log('PDF:', file.name);
          console.log('Base64 longitud:', base64.length);
          console.log('Base64 (últimos 200 caracteres):', base64.slice(-20));
          console.log('Base64 COMPLETO:', base64);
          console.log('===================================');
        }).catch(error => {
          console.error('Error al convertir PDF a Base64 al cargar:', error);
          setError('Error al procesar el PDF: ' + error.message);
        });
      } else {
        console.log('Archivo ignorado (no es XML ni PDF):', file.name, 'tipo:', file.type);
      }
    });
  };

  // Función para eliminar archivo
  const handleRemoveFile = (fileId) => {
    setArchivosFactura(prev => prev.filter(archivo => archivo.id !== fileId));
    // Si se elimina el XML de factura, resetear el estado
    if (xmlFactura && archivosFactura.find(f => f.id === fileId && (f.type === 'text/xml' || f.name.endsWith('.xml')))) {
      setXmlFactura(null);
      setTotalFactura(0);
      setSubtotalFactura(0);
      setImpuestosFactura({ total: 0, traslados: 0, retenciones: 0 });
    }
  };

  // Función para procesar archivo Excel y auto-poblar bitácoras
  const handleExcelUpload = async (file, form) => {
    try {
      setCargandoExcel(true);
      setError(null);

      // Leer el archivo Excel
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          console.log('Datos del Excel:', jsonData);

          // Procesar cada fila del Excel
          const bitacorasData = [];
          for (const row of jsonData) {
            const bitacoraText = row.BITACORA || row.bitacora || '';
            const importe = row.TOTAL || row.total || 0;
            const producto = row.PRODUCTO || row.producto || '';

            // Parsear el texto de bitácora para obtener las claves
            // Formato: "DP298018 / DP298019"
            const bitacoraKeys = bitacoraText.split('/').map(key => key.trim()).filter(k => k);

            if (bitacoraKeys.length > 0 && importe > 0) {
              // Buscar la bitácora en la API usando la primera clave
              const bitacoraKey = bitacoraKeys[0];
              console.log(`Buscando bitácora: ${bitacoraKey}`);

              let matchedBitacora = null;
              let fechaDesdeAPI = null;

              try {
                const bitacoras = await buscarBitacoras(bitacoraKey);
                matchedBitacora = bitacoras.find(b =>
                  b.claveBitacora?.toString().includes(bitacoraKey) ||
                  `${b.serieBitacora}${b.folioBitacora}`.includes(bitacoraKey)
                );

                if (matchedBitacora) {
                  // Formatear fecha correctamente
                  let fecha = matchedBitacora.fecha;
                  if (fecha) {
                    // Si la fecha viene en formato completo, extraer solo la fecha
                    if (fecha.includes('T')) {
                      fecha = fecha.split('T')[0];
                    } else if (fecha.length > 10) {
                      fecha = fecha.substring(0, 10);
                    }
                    fechaDesdeAPI = fecha;
                  }
                  console.log(`Bitácora encontrada en API:`, matchedBitacora);
                  console.log(`Fecha desde API:`, fechaDesdeAPI);
                } else {
                  console.warn(`Bitácora no encontrada en API: ${bitacoraKey} - Se usará la clave del Excel`);
                }
              } catch (err) {
                console.error(`Error al buscar bitácora ${bitacoraKey} en API:`, err);
                console.warn('Continuando con la clave del Excel...');
              }

              // Siempre agregar la bitácora, ya sea de la API o del Excel
              const bitacoraToAdd = matchedBitacora || {
                claveBitacora: bitacoraKey,
                serieBitacora: '',
                folioBitacora: '',
                origenNombre: '',
                destinoNombre: '',
                operadorNombre: '',
                fecha: null
              };

              bitacorasData.push({
                claveBitacora: bitacoraToAdd,
                importe: importe,
                fecha: fechaDesdeAPI || new Date().toISOString().split('T')[0],
                producto: producto,
                casetas: row.CASETAS || row.casetas || 0
              });

              console.log('Bitácora agregada:', {
                clave: bitacoraKey,
                importe: importe,
                fecha: fechaDesdeAPI || new Date().toISOString().split('T')[0],
                producto: producto,
                casetas: row.CASETAS || row.casetas || 0
              });
            }
          }

          console.log('Bitácoras procesadas:', bitacorasData);

          // Auto-poblar el formulario con los datos del Excel
          if (bitacorasData.length > 0 && form) {
            // Agrupar bitácoras por producto para crear conceptos
            const groupedByProduct = bitacorasData.reduce((acc, bitacora) => {
              const key = bitacora.producto || 'GENERAL';
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(bitacora);
              return acc;
            }, {});

            // Crear conceptos basados en los productos agrupados
            const conceptos = Object.keys(groupedByProduct).map((producto, conceptoIndex) => {
              const bitacoras = groupedByProduct[producto];
              const totalImporte = bitacoras.reduce((sum, b) => sum + parseFloat(b.importe || 0), 0);

              return {
                clave: null, // El usuario debe seleccionar el concepto
                importe: totalImporte,
                bitacorasViaje: bitacoras.map((bitacora, bitacoraIndex) => {
                  // Obtener información de la clave bitácora
                  let claveBitacoraText = '';
                  if (bitacora.claveBitacora) {
                    // Si la bitácora tiene serieBitacora y folioBitacora, usarlos
                    if (bitacora.claveBitacora.serieBitacora && bitacora.claveBitacora.folioBitacora) {
                      claveBitacoraText = `${bitacora.claveBitacora.serieBitacora}${bitacora.claveBitacora.folioBitacora}`;
                    } else if (bitacora.claveBitacora.claveBitacora) {
                      // Si es un string o número (de la API o Excel), usarlo directamente
                      claveBitacoraText = bitacora.claveBitacora.claveBitacora.toString();
                    } else {
                      // Si no tiene claveBitacora como propiedad, usar el objeto completo como texto
                      claveBitacoraText = 'N/A';
                    }
                  } else {
                    claveBitacoraText = 'N/A';
                  }

                  // Obtener información de origen y destino si están disponibles
                  const origenDestino = bitacora.claveBitacora?.origenNombre && bitacora.claveBitacora?.destinoNombre
                    ? ` (${bitacora.claveBitacora.origenNombre} → ${bitacora.claveBitacora.destinoNombre})`
                    : '';

                  return {
                    claveBitacora: bitacora.claveBitacora,
                    fecha: bitacora.fecha,
                    importe: parseFloat(bitacora.importe || 0),
                    detalles: [
                      {
                        descripcion: `${producto} - Casetas: ${bitacora.casetas || 'N/A'} - Bitácora: ${claveBitacoraText}${origenDestino}`,
                        fecha: bitacora.fecha,
                        importe: parseFloat(bitacora.importe || 0)
                      }
                    ]
                  };
                })
              };
            });

            // Actualizar el formulario con los nuevos datos
            form.setFieldValue('conceptos', conceptos);

            console.log('Formulario actualizado con datos del Excel');
          }

          setCargandoExcel(false);
        } catch (err) {
          console.error('Error al procesar Excel:', err);
          setError('Error al procesar el archivo Excel: ' + err.message);
          setCargandoExcel(false);
        }
      };
      reader.readAsBinaryString(file);
    } catch (err) {
      console.error('Error al cargar Excel:', err);
      setError('Error al cargar el archivo Excel');
      setCargandoExcel(false);
    }
  };

  // Función robusta para procesar archivo Excel y detectar bloques
  const handleExcelImportRobusto = async (file, currentValues, currentSetFieldValue) => {
    try {
      setCargandoExcel(true);
      setError(null);

      console.log('Detectando tablas en el Excel...');
      const parseResult = await parseExcelFile(file);
      const { todasLasHojas } = parseResult;

      console.log(`Se detectaron ${todasLasHojas.length} hojas en el Excel.`);

      activeFormRef.current = { values: currentValues, setFieldValue: currentSetFieldValue }; // Guardar referencia al formulario actual
      setExcelSheetsData(todasLasHojas);

      // Auto-seleccionar la primera hoja que tenga tablas, si no, la primera del libro
      const dSheet = todasLasHojas.find(s => s.bloquesEncontrados.length > 0) || todasLasHojas[0];
      setSelectedSheetName(dSheet.sheetName);

      // Siempre renderizar el Dialogo para dar la opcion al usuario de elegir modo automatico o manual
      setShowBlockSelector(true);

      setCargandoExcel(false);
    } catch (err) {
      console.error('Error al importar Excel robusto:', err);
      setError('Error al importar Excel (robusto): ' + err.message);
      setCargandoExcel(false);
    }
  };

  // Función interna para procesar el bloque final una vez elegido (o si solo hubo 1)
  const procesarBloqueSeleccionado = (rawData, bloque) => {
    try {
      const bitacoras = extraeBloqueEspecifico(rawData, bloque);

      console.log(`=================================================`);
      console.log(`🚀 RESULTADO DE LECTURA DEL BLOQUE SELECCIONADO`);
      console.log(`Se extrajeron ${bitacoras.length} registros válidos.`);
      console.dir(bitacoras, { depth: null });
      console.log(`=================================================`);

      // NUEVO: Verificación de múltiples apoyos
      const apoyosUnicos = [...new Set(bitacoras.map(b => {
        const val = b.apoyoKms;
        return typeof val === 'number' ? val.toString() : String(val || "").trim();
      }).filter(val => val !== "" && val !== "0" && val !== "0.00" && val !== "NaN"))];

      if (apoyosUnicos.length > 1) {
        setApoyosDisponibles(apoyosUnicos);
        setBitacorasPendientesPorApoyo(bitacoras);
        setShowApoyoSelector(true);
        return; // Esperar a que el usuario seleccione
      }

      continuarImportacionConBitacoras(bitacoras);
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  const continuarImportacionConBitacoras = (bitacoras) => {
    try {
      const formikActions = activeFormRef.current;
      if (!formikActions) {
        console.warn("No form ref attached");
        alert(`¡Tabla leída! ${bitacoras.length} registros, pero no hay formulario activo.`);
        return;
      }

      const currentConceptos = [...formikActions.values.conceptos];

      // Validar que al menos un concepto tenga clave
      const isAnyConceptReady = currentConceptos.some(c => c.clave);
      if (!isAnyConceptReady) {
        alert("⚠️ Selecciona primero la 'Clave del Concepto' en el formulario para poder mapear los datos del Excel.");
        setShowBlockSelector(false);
        return;
      }

      const { newConceptos, mappedCount } = mapearBitacorasAConceptos(bitacoras, currentConceptos, formikActions.values.fechaEmision);

      formikActions.setFieldValue('conceptos', newConceptos);
      alert(`¡Excel Mapeado Exitosamente!\nSe extrajeron ${bitacoras.length} registros (ya filtrados) y se mapearon ${mappedCount} atributos en total.`);

      setShowBlockSelector(false); // Cerramos el dialogo si viene de ahi
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  // Función para convertir archivo a Base64 de forma robusta
  // Usa ArrayBuffer para evitar truncamiento en archivos grandes
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const arrayBuffer = reader.result;

          // Verificar que sea un ArrayBuffer válido
          if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) {
            reject(new Error('El resultado de la lectura no es un ArrayBuffer válido'));
            return;
          }

          // Convertir ArrayBuffer a Base64 usando conversión directa desde bytes
          // Este método evita problemas de memoria y truncamiento
          const bytes = new Uint8Array(arrayBuffer);

          console.log(`Convirtiendo ${bytes.length} bytes a Base64...`);

          // Conversión directa desde bytes a Base64 sin pasar por string binario
          // Esto es más eficiente y evita problemas de memoria
          const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
          let base64 = '';

          // Procesar en grupos de 3 bytes
          for (let i = 0; i < bytes.length; i += 3) {
            const remainingBytes = bytes.length - i;

            if (remainingBytes >= 3) {
              // Caso normal: 3 bytes completos -> 4 caracteres Base64
              const byte1 = bytes[i];
              const byte2 = bytes[i + 1];
              const byte3 = bytes[i + 2];
              const bitmap = (byte1 << 16) | (byte2 << 8) | byte3;

              base64 += base64Chars.charAt((bitmap >> 18) & 63);
              base64 += base64Chars.charAt((bitmap >> 12) & 63);
              base64 += base64Chars.charAt((bitmap >> 6) & 63);
              base64 += base64Chars.charAt(bitmap & 63);
            } else if (remainingBytes === 2) {
              // Caso: 2 bytes restantes -> 3 caracteres Base64 + 1 '='
              const byte1 = bytes[i];
              const byte2 = bytes[i + 1];
              const bitmap = (byte1 << 16) | (byte2 << 8);

              base64 += base64Chars.charAt((bitmap >> 18) & 63);
              base64 += base64Chars.charAt((bitmap >> 12) & 63);
              base64 += base64Chars.charAt((bitmap >> 6) & 63);
              base64 += '=';
            } else if (remainingBytes === 1) {
              // Caso: 1 byte restante -> 2 caracteres Base64 + 2 '=='
              const byte1 = bytes[i];
              const bitmap = (byte1 << 16);

              base64 += base64Chars.charAt((bitmap >> 18) & 63);
              base64 += base64Chars.charAt((bitmap >> 12) & 63);
              base64 += '==';
            }

            // Log de progreso cada 1MB procesado
            if (i > 0 && i % (1024 * 1024) === 0) {
              const progress = Math.round((i / bytes.length) * 100);
              console.log(`Progreso conversión Base64: ${progress}% (${i}/${bytes.length} bytes)`);
            }
          }

          console.log(`✅ Conversión completada: ${base64.length} caracteres Base64 generados`);

          // Verificar que el Base64 no esté vacío
          if (!base64 || base64.length === 0) {
            reject(new Error('El Base64 del archivo está vacío'));
            return;
          }

          // Validar que el Base64 sea válido (solo caracteres Base64 válidos)
          console.log('=== Validando caracteres Base64 ===');
          const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;

          // Buscar caracteres inválidos ANTES de validar con regex
          const invalidChars = [];
          for (let i = 0; i < base64.length; i++) {
            const char = base64[i];
            if (!/[A-Za-z0-9+/=]/.test(char)) {
              invalidChars.push({
                index: i,
                char: char,
                charCode: char.charCodeAt(0),
                hex: '0x' + char.charCodeAt(0).toString(16).toUpperCase()
              });
              // Limitar a los primeros 20 caracteres inválidos para no saturar la consola
              if (invalidChars.length >= 20) break;
            }
          }

          if (invalidChars.length > 0) {
            console.error('❌ SE ENCONTRARON CARACTERES INVÁLIDOS EN EL BASE64 DEL PDF');
            console.error(`Total de caracteres inválidos encontrados: ${invalidChars.length} (mostrando los primeros 20)`);
            console.error('Caracteres inválidos:', invalidChars);

            // Mostrar contexto alrededor de cada carácter inválido
            invalidChars.forEach((invalid, idx) => {
              const start = Math.max(0, invalid.index - 30);
              const end = Math.min(base64.length, invalid.index + 30);
              const context = base64.substring(start, end);
              const marker = '^'.padStart(invalid.index - start + 1);
              console.error(`\nCarácter inválido #${idx + 1} (índice ${invalid.index}):`);
              console.error(`  Carácter: "${invalid.char}" (código: ${invalid.charCode}, hex: ${invalid.hex})`);
              console.error(`  Contexto: ${context}`);
              console.error(`  Posición: ${marker}`);
            });

            // Mostrar estadísticas
            const charCounts = {};
            invalidChars.forEach(inv => {
              const key = `${inv.char} (${inv.charCode})`;
              charCounts[key] = (charCounts[key] || 0) + 1;
            });
            console.error('\nEstadísticas de caracteres inválidos:', charCounts);

            reject(new Error(`El Base64 generado contiene ${invalidChars.length} caracteres inválidos. Ver consola para detalles.`));
            return;
          } else {
            console.log('✅ No se encontraron caracteres inválidos en el Base64');
          }

          // Validar con regex también
          if (!base64Regex.test(base64)) {
            console.error('❌ Base64 falla validación con regex (pero no se encontraron caracteres inválidos en el escaneo)');
            console.error('Esto podría indicar un problema con el padding o la estructura del Base64');
            reject(new Error('El Base64 generado no pasa la validación de formato'));
            return;
          }

          // Verificar padding correcto
          // Base64 válido debe tener longitud múltiplo de 4, o terminar con = o ==
          const remainder = base64.length % 4;
          const lastChar = base64.slice(-1);
          const lastTwoChars = base64.slice(-2);

          if (remainder === 1) {
            // Longitud módulo 4 = 1 es inválido (debería ser 0, 2, o 3)
            reject(new Error('Base64 tiene formato inválido (longitud incorrecta, remainder=1)'));
            return;
          }

          // Validar padding según el remainder
          if (remainder === 0) {
            // Sin remainder, puede terminar con cualquier carácter Base64 válido (sin padding o con = o ==)
            // Esto es válido
            console.log('Base64 sin remainder (múltiplo de 4) - padding opcional');
          } else if (remainder === 2) {
            // Debe terminar con ==
            if (!base64.endsWith('==')) {
              console.warn('⚠️ Base64 con remainder=2 debería terminar con ==, pero termina con:', lastTwoChars);
            } else {
              console.log('✅ Base64 con padding correcto (==)');
            }
          } else if (remainder === 3) {
            // Debe terminar con =
            if (!base64.endsWith('=')) {
              console.warn('⚠️ Base64 con remainder=3 debería terminar con =, pero termina con:', lastChar);
            } else {
              console.log('✅ Base64 con padding correcto (=)');
            }
          }

          // Mostrar información del padding
          if (base64.endsWith('==')) {
            console.log('   - Padding: == (2 caracteres)');
          } else if (base64.endsWith('=')) {
            console.log('   - Padding: = (1 carácter)');
          } else {
            console.log('   - Padding: ninguno (longitud es múltiplo de 3)');
          }

          // Verificar que la longitud sea exacta
          // Base64 debe tener exactamente: Math.ceil(file.size / 3) * 4 caracteres
          const expectedLength = Math.ceil(file.size / 3) * 4;

          console.log(`Verificación de longitud Base64:`);
          console.log(`  - Tamaño archivo: ${file.size} bytes`);
          console.log(`  - Longitud Base64 esperada (exacta): ${expectedLength} caracteres`);
          console.log(`  - Longitud Base64 obtenida: ${base64.length} caracteres`);
          console.log(`  - Diferencia: ${base64.length - expectedLength} caracteres`);

          if (base64.length !== expectedLength) {
            console.error(`❌ Base64 tiene longitud incorrecta. Esperado: ${expectedLength}, Obtenido: ${base64.length}`);
            if (base64.length < expectedLength) {
              console.error(`   - Faltan ${expectedLength - base64.length} caracteres (TRUNCADO)`);
              reject(new Error(`El Base64 está truncado. Esperado: ${expectedLength}, Obtenido: ${base64.length}`));
              return;
            } else {
              console.error(`   - Sobran ${base64.length - expectedLength} caracteres`);
              reject(new Error(`El Base64 tiene longitud incorrecta. Esperado: ${expectedLength}, Obtenido: ${base64.length}`));
              return;
            }
          } else {
            console.log(`✅ Longitud Base64 correcta (${base64.length} caracteres)`);
          }

          // Calcular qué padding debería tener según el tamaño del archivo
          const fileSizeMod3 = file.size % 3;
          let expectedPadding = '';
          if (fileSizeMod3 === 1) {
            expectedPadding = '==';
          } else if (fileSizeMod3 === 2) {
            expectedPadding = '=';
          } else {
            expectedPadding = 'ninguno';
          }

          console.log(`✅ Base64 del archivo ${file.name}:`);
          console.log(`   - Longitud: ${base64.length} caracteres`);
          console.log(`   - Tamaño original: ${file.size} bytes`);
          console.log(`   - Tamaño módulo 3: ${fileSizeMod3} (padding esperado: ${expectedPadding})`);
          console.log(`   - Ratio: ${((base64.length / file.size) * 100).toFixed(2)}%`);
          console.log(`   - Termina con: ${base64.slice(-20)}`);
          console.log(`   - ¿Termina con ==? ${base64.endsWith('==') ? 'Sí ✅' : 'No (esto es normal si el archivo no requiere ==)'}`);

          resolve(base64);
        } catch (error) {
          console.error('Error al procesar Base64:', error);
          console.error('Stack:', error.stack);
          reject(new Error('Error al convertir el archivo a Base64: ' + error.message));
        }
      };

      reader.onerror = (error) => {
        console.error('Error al leer el archivo:', error);
        console.error('Error target:', error.target);
        reject(new Error('Error al leer el archivo: ' + (error.message || 'Error desconocido')));
      };

      reader.onabort = () => {
        reject(new Error('La lectura del archivo fue cancelada'));
      };

      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentLoaded = Math.round((event.loaded / event.total) * 100);
          if (percentLoaded % 25 === 0) { // Log cada 25%
            console.log(`Leyendo archivo ${file.name}: ${percentLoaded}%`);
          }
        }
      };

      // Usar readAsArrayBuffer para evitar truncamiento en archivos grandes
      console.log(`Iniciando lectura del archivo ${file.name} (${file.size} bytes)...`);
      reader.readAsArrayBuffer(file);
    });
  };

  // Función para convertir string UTF-8 a Base64 de forma segura
  const stringToBase64 = (str) => {
    try {
      // Método más robusto: usar TextEncoder para UTF-8 y luego convertir a Base64
      if (typeof TextEncoder !== 'undefined') {
        const encoder = new TextEncoder();
        const bytes = encoder.encode(str);

        // Convertir bytes a string binario de forma segura
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }

        return btoa(binary);
      }

      // Fallback para navegadores sin TextEncoder
      // Codificar primero a URI para manejar caracteres UTF-8
      return btoa(unescape(encodeURIComponent(str)));
    } catch (error) {
      console.error('Error al convertir string a Base64:', error);
      // Último fallback: método tradicional
      try {
        return btoa(unescape(encodeURIComponent(str)));
      } catch (fallbackError) {
        console.error('Error en fallback de Base64:', fallbackError);
        throw new Error('No se pudo convertir el XML a Base64: ' + fallbackError.message);
      }
    }
  };

  // Función para formatear XML con indentación mejorada
  const formatXML = (xmlString) => {
    try {
      // Validar que el XML no esté vacío
      if (!xmlString || typeof xmlString !== 'string' || xmlString.trim() === '') {
        // No loguear si es un string vacío (es normal cuando xmlPreview está vacío inicialmente)
        return xmlString || '';
      }

      // Validar que parezca XML (debe empezar con <)
      if (!xmlString.trim().startsWith('<')) {
        console.warn('El string no parece ser XML válido en formatXML');
        return xmlString;
      }

      // Si el XML está en una sola línea, intentar formatearlo
      if (!xmlString.includes('\n') || xmlString.split('\n').length <= 3) {
        const cleanedXML = xmlString.replace(/>\s+</g, '><').trim();

        // Validar que el XML limpiado no esté vacío
        if (!cleanedXML || cleanedXML === '') {
          return xmlString;
        }

        // Usar un parser DOMParser para formatear mejor
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(cleanedXML, 'text/xml');

        // Verificar errores de parsing
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          console.warn('Error al parsear XML en formatXML, retornando original');
          return xmlString;
        }

        // Verificar que el documento tenga un elemento raíz
        if (!xmlDoc.documentElement) {
          console.warn('XML no tiene elemento raíz en formatXML, retornando original');
          return xmlString;
        }

        if (xmlDoc.documentElement) {
          let formatted = '';

          const formatNode = (node, indent = '') => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              let result = indent + '<' + node.nodeName;

              // Agregar atributos
              if (node.attributes) {
                for (let i = 0; i < node.attributes.length; i++) {
                  const attr = node.attributes[i];
                  result += ` ${attr.name}="${attr.value}"`;
                }
              }

              const children = Array.from(node.childNodes).filter(n =>
                n.nodeType === Node.ELEMENT_NODE ||
                (n.nodeType === Node.TEXT_NODE && n.textContent.trim())
              );

              if (children.length === 0) {
                result += '/>\n';
              } else {
                result += '>\n';

                for (const child of children) {
                  result += formatNode(child, indent + '  ');
                }

                result += indent + '</' + node.nodeName + '>\n';
              }

              return result;
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
              return indent + node.textContent.trim() + '\n';
            }

            return '';
          };

          formatted = '<?xml version="1.0" encoding="UTF-8"?>\n';
          formatted += formatNode(xmlDoc.documentElement);

          return formatted;
        }
      }

      // Si ya está formateado o hay error, retornar el original
      return xmlString;
    } catch (error) {
      console.error('Error al formatear XML:', error);
      // Si falla, intentar formateo simple
      const formatted = xmlString.replace(/><([^/])/g, '>\n<$1');
      return formatted;
    }
  };

  const generateAddendaXML = (values) => {
    // Si hay XML de factura, integrar la adenda directamente en él; si no, generar
    // una adenda suelta. La integración lanza si falla (en lugar de enviar en
    // silencio una adenda suelta mal formada); los llamadores manejan el error.
    if (xmlFactura) {
      return integrarAddendaEnFactura(xmlFactura, values.conceptos);
    }
    return generarAddendaStandalone(values.conceptos);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);

    try {
      // Si hay XML de factura, validar que los importes de los conceptos coincidan
      if (xmlFactura) {
        const impuestosFactura = extraerImpuestosFactura(xmlFactura);
        const conceptosFactura = extraerConceptosFactura(xmlFactura);

        console.log('Impuestos de la factura:', impuestosFactura);
        console.log('Conceptos de la factura:', conceptosFactura);

        // Calcular la suma de importes de los conceptos de la adenda
        const sumaConceptosAdenda = values.conceptos.reduce((suma, concepto) => {
          return suma + (parseFloat(concepto.importe) || 0);
        }, 0);

        console.log('Suma de conceptos de la adenda:', sumaConceptosAdenda);

        // Si hay impuestos en la factura, verificar que coincidan con algún concepto de la adenda
        // El backend compara el importe del concepto "Impuesto" con el importe de algún concepto de la adenda
        if (impuestosFactura.total > 0 || impuestosFactura.traslados > 0) {
          // Obtener todos los importes de los conceptos en la adenda
          const importesConceptos = values.conceptos.map((concepto, index) => ({
            index: index + 1,
            importe: parseFloat(concepto.importe) || 0,
            clave: concepto.clave?.CodigoGasto || concepto.clave || 'Sin clave'
          }));

          // Buscar si hay algún concepto en la adenda que coincida exactamente con el importe del impuesto
          const conceptoImpuestoCoincide = values.conceptos.some(concepto => {
            const importeConcepto = parseFloat(concepto.importe) || 0;
            // Comparar con el total de impuestos (traslados - retenciones) y con traslados
            return Math.abs(importeConcepto - impuestosFactura.total) < 0.01 ||
              Math.abs(importeConcepto - impuestosFactura.traslados) < 0.01;
          });

          if (!conceptoImpuestoCoincide && impuestosFactura.traslados > 0) {
            // Construir mensaje detallado con todos los importes
            const listaImportes = importesConceptos.map(c =>
              `Concepto ${c.index} (${c.clave}): $${c.importe.toFixed(2)}`
            ).join('\n');

            const mensajeError = `❌ Error de validación de impuestos:\n\n` +
              `El importe del concepto (Impuesto) en la factura es: $${impuestosFactura.traslados.toFixed(2)}\n\n` +
              `Importes de conceptos en la adenda:\n${listaImportes}\n\n` +
              `Suma total de conceptos: $${sumaConceptosAdenda.toFixed(2)}\n\n` +
              `⚠️ El backend requiere que AL MENOS UN concepto en la adenda tenga exactamente el mismo importe que el impuesto de la factura ($${impuestosFactura.traslados.toFixed(2)}).\n\n` +
              `Por favor, ajusta uno de los conceptos para que tenga el importe de $${impuestosFactura.traslados.toFixed(2)}.`;

            console.error('❌ Error de validación:', mensajeError);
            console.error('Detalles:', {
              impuestoFactura: impuestosFactura.traslados,
              importesConceptos: importesConceptos,
              sumaTotal: sumaConceptosAdenda
            });
            setError(mensajeError);
            setLoading(false);
            return;
          }

          // Si hay coincidencia, loguear para debugging
          if (conceptoImpuestoCoincide) {
            const conceptoCoincidente = values.conceptos.find(concepto => {
              const importeConcepto = parseFloat(concepto.importe) || 0;
              return Math.abs(importeConcepto - impuestosFactura.traslados) < 0.01 ||
                Math.abs(importeConcepto - impuestosFactura.total) < 0.01;
            });
            console.log('✅ Validación de impuestos OK:', {
              impuestoFactura: impuestosFactura.traslados,
              conceptoCoincidente: conceptoCoincidente ? {
                importe: conceptoCoincidente.importe,
                clave: conceptoCoincidente.clave?.CodigoGasto || conceptoCoincidente.clave
              } : 'No encontrado'
            });
          }
        }

        // Validar que la suma de conceptos de la adenda coincida con el SubTotal de la factura
        const diferencia = Math.abs(sumaConceptosAdenda - subtotalFactura);

        if (diferencia > 0.01 && subtotalFactura > 0) {
          console.warn(`⚠️ Advertencia: La suma de conceptos de la adenda (${sumaConceptosAdenda.toFixed(2)}) no coincide con el SubTotal de la factura (${subtotalFactura.toFixed(2)})`);
        }
      }

      // Generar XML de la adenda
      const addendaXML = generateAddendaXML(values);

      // Validar que el XML no esté vacío
      if (!addendaXML || addendaXML.trim() === '') {
        throw new Error('El XML generado está vacío');
      }

      // Validar que el XML esté bien formado antes de enviarlo, para no mandar al
      // webservice una adenda mal formada que solo produce un error genérico.
      const validationDoc = new DOMParser().parseFromString(addendaXML, 'text/xml');
      const wellFormedError = validationDoc.querySelector('parsererror');
      if (wellFormedError) {
        console.error('El XML de la adenda no está bien formado:', wellFormedError.textContent);
        throw new Error('El XML de la adenda generado no está bien formado');
      }

      // Convertir a Base64 usando función segura para UTF-8
      const xmlBase64 = stringToBase64(addendaXML);

      // Validar que el Base64 sea válido (solo contiene caracteres Base64 válidos)
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(xmlBase64)) {
        console.error('Base64 generado contiene caracteres inválidos');
        throw new Error('Error al generar Base64 válido del XML');
      }

      console.log('XML generado (primeros 200 caracteres):', addendaXML.substring(0, 200));
      console.log('Base64 generado (primeros 100 caracteres):', xmlBase64.substring(0, 100));
      console.log('Longitud del Base64:', xmlBase64.length);

      // Procesar archivos PDF
      console.log('=== Procesando archivos PDF ===');
      console.log('Total de archivos cargados:', archivosFactura.length);
      console.log('Archivos cargados:', archivosFactura.map(a => ({ name: a.name, type: a.type })));

      let pdfBase64 = "";
      const pdfFiles = archivosFactura.filter(archivo => {
        const isPDF = archivo.type === 'application/pdf' || archivo.name.endsWith('.pdf');
        console.log(`Archivo ${archivo.name}: type=${archivo.type}, isPDF=${isPDF}`);
        return isPDF;
      });

      console.log('Archivos PDF encontrados:', pdfFiles.length);

      if (pdfFiles.length > 0) {
        console.log('Procesando PDF:', pdfFiles[0].name);
        console.log('Tamaño del PDF:', pdfFiles[0].file?.size || 'file no encontrado');

        if (!pdfFiles[0].file) {
          console.error('❌ El archivo PDF no tiene la propiedad file');
          console.error('Estructura del objeto:', pdfFiles[0]);
          throw new Error('El archivo PDF no está disponible');
        }

        try {
          console.log('Llamando a fileToBase64 para el PDF...');
          pdfBase64 = await fileToBase64(pdfFiles[0].file);
          console.log('✅ fileToBase64 completado para PDF');

          // Validar que el Base64 del PDF sea válido
          const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
          if (!base64Regex.test(pdfBase64)) {
            console.warn('⚠️ Base64 del PDF contiene caracteres inválidos');
          }

          console.log('=== BASE64 DEL PDF ===');
          console.log('PDF Base64 generado: longitud=', pdfBase64.length);
          console.log('PDF Base64 (últimos 200 caracteres):', pdfBase64.slice(-20));
          console.log('PDF Base64 COMPLETO:', pdfBase64);
          console.log('=====================');

          // Verificar que el Base64 no esté truncado (debe terminar con =, ==, o caracteres alfanuméricos)
          if (pdfBase64.length > 0 && !base64Regex.test(pdfBase64)) {
            throw new Error('El Base64 del PDF está corrupto o truncado');
          }
        } catch (error) {
          console.error('❌ Error al procesar PDF:', error);
          console.error('Stack:', error.stack);
          setError('Error al procesar el archivo PDF: ' + error.message);
          setLoading(false);
          return;
        }
      } else {
        console.log('⚠️ No se encontraron archivos PDF para procesar');
      }

      // Crear el payload para la API
      const factura = {
        ClaveTipoFactura: parseInt(values.claveTipoFactura),
        RemitenteRFC: bitacoraData.remitenteRFC || auth.user,
        Estatus: parseInt(values.estatus),
        attachPathPDF: pdfBase64,
        attachPathXML: xmlBase64
      };

      // Validar longitudes de Base64 antes de enviar
      console.log('=== Validación antes de enviar ===');
      console.log('XML Base64 longitud:', factura.attachPathXML.length);
      console.log('XML Base64 termina con:', factura.attachPathXML.slice(-4));
      console.log('PDF Base64 longitud:', factura.attachPathPDF.length);
      console.log('PDF Base64 termina con:', factura.attachPathPDF.slice(-4));

      // Verificar que los Base64 no estén vacíos (si se espera que tengan contenido)
      if (factura.attachPathXML.length === 0) {
        throw new Error('El Base64 del XML está vacío');
      }

      // El PDF puede estar vacío si no se cargó, eso está bien
      if (pdfFiles.length > 0 && factura.attachPathPDF.length === 0) {
        throw new Error('El Base64 del PDF está vacío aunque se cargó un archivo');
      }

      // Verificar que el Base64 tenga el formato correcto (debe terminar con =, ==, o caracteres alfanuméricos)
      // El padding de Base64 es necesario cuando la longitud no es múltiplo de 4
      const validateBase64Padding = (base64) => {
        if (base64.length === 0) return true; // String vacío está bien
        // Base64 válido debe tener longitud múltiplo de 4, o terminar con = o ==
        const remainder = base64.length % 4;
        if (remainder === 0) {
          // Longitud correcta, puede terminar con cualquier carácter Base64 válido
          return /^[A-Za-z0-9+/]*={0,2}$/.test(base64);
        } else if (remainder === 1) {
          // Debe terminar con =
          return base64.endsWith('=') && /^[A-Za-z0-9+/]*=$/.test(base64);
        } else if (remainder === 2) {
          // Debe terminar con ==
          return base64.endsWith('==') && /^[A-Za-z0-9+/]*==$/.test(base64);
        } else {
          // remainder === 3, no debería tener padding pero puede tenerlo
          return /^[A-Za-z0-9+/]*={0,2}$/.test(base64);
        }
      };

      if (!validateBase64Padding(factura.attachPathXML)) {
        console.error('XML Base64 tiene formato inválido o está truncado');
        console.error('Últimos 10 caracteres:', factura.attachPathXML.slice(-10));
        throw new Error('El Base64 del XML tiene formato inválido o está truncado');
      }

      if (factura.attachPathPDF.length > 0 && !validateBase64Padding(factura.attachPathPDF)) {
        console.error('PDF Base64 tiene formato inválido o está truncado');
        console.error('Últimos 10 caracteres:', factura.attachPathPDF.slice(-10));
        throw new Error('El Base64 del PDF tiene formato inválido o está truncado');
      }

      // Verificar que el payload completo se pueda serializar correctamente
      try {
        const payloadString = JSON.stringify([factura]);
        console.log('=== Validación después de serializar ===');
        console.log('Payload serializado, longitud total:', payloadString.length);

        // Verificar que el Base64 del XML esté completo en el payload serializado
        const xmlEndMarker = factura.attachPathXML.slice(-50);
        if (!payloadString.includes(xmlEndMarker)) {
          console.error('❌ El Base64 del XML NO está completo en el payload serializado');
          console.error('Buscando marcador:', xmlEndMarker);
          throw new Error('El Base64 del XML se truncó al serializar el payload');
        } else {
          console.log('✅ Base64 del XML verificado en payload serializado');
        }

        // Verificar que el Base64 del PDF esté completo en el payload serializado
        if (factura.attachPathPDF.length > 0) {
          const pdfEndMarker = factura.attachPathPDF.slice(-50);
          if (!payloadString.includes(pdfEndMarker)) {
            console.error('❌ El Base64 del PDF NO está completo en el payload serializado');
            console.error('Buscando marcador:', pdfEndMarker);
            console.error('Longitud del PDF Base64 original:', factura.attachPathPDF.length);
            console.error('Longitud del payload serializado:', payloadString.length);

            // Intentar encontrar dónde se truncó
            const pdfStartIndex = payloadString.indexOf('"attachPathPDF":"');
            if (pdfStartIndex !== -1) {
              const pdfEndIndex = payloadString.indexOf('"', pdfStartIndex + 17);
              if (pdfEndIndex !== -1) {
                const pdfInPayload = payloadString.substring(pdfStartIndex + 17, pdfEndIndex);
                console.error('Longitud del PDF en payload:', pdfInPayload.length);
                console.error('Últimos 50 caracteres del PDF en payload:', pdfInPayload.slice(-50));
              }
            }

            throw new Error('El Base64 del PDF se truncó al serializar el payload');
          } else {
            console.log('✅ Base64 del PDF verificado en payload serializado');
            console.log('   - Longitud del PDF Base64 en payload:', payloadString.match(/"attachPathPDF":"([^"]*)"/)?.[1]?.length || 'no encontrado');
          }
        }

        // Verificar que el payload no exceda límites razonables (10MB)
        const payloadSizeMB = payloadString.length / (1024 * 1024);
        if (payloadSizeMB > 10) {
          console.warn(`⚠️ El payload es muy grande: ${payloadSizeMB.toFixed(2)} MB`);
        } else {
          console.log(`✅ Tamaño del payload: ${payloadSizeMB.toFixed(2)} MB`);
        }
      } catch (serializeError) {
        console.error('Error al serializar o validar el payload:', serializeError);
        throw new Error('No se pudo serializar o validar el payload para enviar a la API: ' + serializeError.message);
      }

      const response = await postActualizaDirect([factura]);

      // Check for both success (lowercase) and Success (capital S)
      const isSuccess = response.success === true || response.Success === true;

      if (isSuccess) {
        setSuccess(true);
        setSuccessDialog(true);
      } else {
        const errorMsg = response.message || response.Message || "Error al crear la adenda";
        setError(errorMsg);
        setErrorMessage(errorMsg);
        setErrorDialog(true);
      }
    } catch (err) {
      // Manejar diferentes formatos de error
      let errorMessage = "Error desconocido al crear la adenda";

      // Verificar si el error tiene el formato de Postman (success: false con errors)
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        // Construir mensaje con el mensaje principal y los errores específicos
        const mainMessage = err.response.data.message || err.message || "Error al procesar la solicitud";
        const errorsList = err.response.data.errors.join('\n');
        errorMessage = `${mainMessage}\n${errorsList}`;
      } else if (err.errors && Array.isArray(err.errors)) {
        // Si el error ya tiene el formato procesado (desde postActualiza)
        const mainMessage = err.message || "Error al procesar la solicitud";
        const errorsList = err.errors.join('\n');
        errorMessage = `${mainMessage}\n${errorsList}`;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err.Message) {
        errorMessage = err.Message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.Message) {
        errorMessage = err.response.data.Message;
      }

      console.error("Error completo:", err);
      setError(errorMessage);
      setErrorMessage(errorMessage);
      setErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (values) => {
    try {
      const xml = generateAddendaXML(values);

      // Validar que el XML no esté vacío
      if (!xml || xml.trim() === '') {
        console.error('El XML generado está vacío');
        setError('No se pudo generar el XML de la adenda');
        return;
      }

      // Guardar los valores del formulario para poder enviarlos desde el modal
      setPreviewValues(values);
      setXmlPreview(xml);
      setShowFormatted(true);
      setPreviewDialog(true);
    } catch (error) {
      console.error('Error al generar preview:', error);
      setError('Error al generar la vista previa: ' + error.message);
    }
  };

  const handleDownload = (values) => {
    try {
      const xml = generateAddendaXML(values);

      // Validar que el XML no esté vacío
      if (!xml || xml.trim() === '') {
        console.error('El XML generado está vacío');
        setError('No se pudo generar el XML de la adenda');
        return;
      }

      // Formatear el XML para la descarga
      const formattedXML = formatXML(xml);
      const blob = new Blob([formattedXML], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = xmlFactura
        ? `factura_con_adenda_${bitacoraData.claveBitacora || 'nueva'}_${new Date().toISOString().split('T')[0]}.xml`
        : `adenda_bitacora_${bitacoraData.claveBitacora || 'nueva'}_${new Date().toISOString().split('T')[0]}.xml`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar XML:', error);
      setError('Error al descargar el XML: ' + error.message);
    }
  };

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        py: 4,
        maxWidth: collapsed ? 'calc(100vw - 0px)' : 'calc(100vw - 400px)',
        overflowX: 'hidden',
        transition: 'max-width 0.3s ease',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4">
          Crear Adenda para Bitácora
        </Typography>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          onClick={handleDebugFetch}
          sx={{ ml: 2 }}
        >
          🐛 Debug API
        </Button>
      </Box>

      {bitacoraData.claveBitacora && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Información de la Bitácora
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Clave Bitácora:</strong> {bitacoraData.claveBitacora}
                </Typography>
                <Typography variant="body2">
                  <strong>Serie:</strong> {bitacoraData.serieBitacora || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Folio:</strong> {bitacoraData.folioBitacora || "-"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Origen:</strong> {bitacoraData.origenNombre || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Destino:</strong> {bitacoraData.destinoNombre || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Conductor:</strong> {bitacoraData.operadorNombre || "-"}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}


      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form>
            {/* Campos de configuración de la adenda */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
                  Configuración de la Adenda
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="large" sx={{ minHeight: '56px', minWidth: '200px' }}>
                      <InputLabel sx={{ fontSize: '1.1rem' }}>Tipo de Factura</InputLabel>
                      <Field
                        as={Select}
                        name="claveTipoFactura"
                        label="Tipo de Factura"
                        disabled={cargandoCatalogos}
                        displayEmpty
                        renderValue={(selected) => {
                          // if (!selected || selected === '') {
                          //   return <em>Seleccione un tipo de factura</em>;
                          // }
                          const tipo = tiposFactura.find(t => t.ClaveTipoFactura.toString() === selected);
                          return tipo ? `${tipo.Descripcion} (${tipo.TipoFactura})` : selected;
                        }}
                        sx={{
                          fontSize: '1.1rem',
                          minHeight: '56px',
                          '& .MuiSelect-select': {
                            padding: '16px 14px',
                            fontSize: '1.1rem',
                            minHeight: '24px',
                            display: 'flex',
                            alignItems: 'center'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            minHeight: '56px'
                          }
                        }}
                      >
                        {cargandoCatalogos ? (
                          <MenuItem disabled>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              <span>Cargando tipos...</span>
                            </Box>
                          </MenuItem>
                        ) : (
                          tiposFactura.map((tipo) => (
                            <MenuItem
                              key={tipo.ClaveTipoFactura}
                              value={tipo.ClaveTipoFactura.toString()}
                              sx={{ fontSize: '1.1rem', padding: '12px 16px' }}
                            >
                              {tipo.Descripcion} ({tipo.TipoFactura})
                            </MenuItem>
                          ))
                        )}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="large" sx={{ minHeight: '56px', minWidth: '200px' }}>
                      <InputLabel sx={{ fontSize: '1.1rem' }}>Estatus</InputLabel>
                      <Field
                        as={Select}
                        name="estatus"
                        label="Estatus"
                        disabled={cargandoCatalogos}
                        displayEmpty
                        renderValue={(selected) => {
                          // if (!selected || selected === '') {
                          //   return <em>Seleccione un estatus</em>;
                          // }
                          const estatus = estatusFactura.find(e => e.ClaveEstatusFactura.toString() === selected);
                          return estatus ? estatus.EstatusFactura : selected;
                        }}
                        sx={{
                          fontSize: '1.1rem',
                          minHeight: '56px',
                          '& .MuiSelect-select': {
                            padding: '16px 14px',
                            fontSize: '1.1rem',
                            minHeight: '24px',
                            display: 'flex',
                            alignItems: 'center'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            minHeight: '56px'
                          }
                        }}
                      >
                        {cargandoCatalogos ? (
                          <MenuItem disabled>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              <span>Cargando estatus...</span>
                            </Box>
                          </MenuItem>
                        ) : (
                          estatusFactura.map((estatus) => (
                            <MenuItem
                              key={estatus.ClaveEstatusFactura}
                              value={estatus.ClaveEstatusFactura.toString()}
                              sx={{ fontSize: '1.1rem', padding: '12px 16px' }}
                            >
                              {estatus.EstatusFactura}
                            </MenuItem>
                          ))
                        )}
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Sección de carga de archivos de factura */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
                  📎 Archivos de Factura
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <input
                    accept=".xml,.pdf"
                    style={{ display: 'none' }}
                    id="upload-files"
                    type="file"
                    onChange={(e) => handleFileUpload(e, setFieldValue)}
                  />
                  <input
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    id="upload-excel"
                    type="file"
                  />
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <label htmlFor="upload-files">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                      >
                        Cargar XML y PDF de Factura
                      </Button>
                    </label>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Formatos soportados: XML, PDF
                  </Typography>
                </Box>

                {/* Botón para cargar Excel de Bitácoras - Se agregará dentro del Form */}

                {archivosFactura.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Archivos Cargados ({archivosFactura.length})
                    </Typography>
                    <List>
                      {archivosFactura.map((archivo) => (
                        <ListItem key={archivo.id} divider>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                {archivo.type === 'application/pdf' || archivo.name.endsWith('.pdf') ? (
                                  <PdfIcon color="error" />
                                ) : (
                                  <XmlIcon color="primary" />
                                )}
                                <Typography variant="body1" fontWeight="medium">
                                  {archivo.name}
                                </Typography>
                                <Chip
                                  label={archivo.type === 'application/pdf' || archivo.name.endsWith('.pdf') ? 'PDF' : 'XML'}
                                  size="small"
                                  color={archivo.type === 'application/pdf' || archivo.name.endsWith('.pdf') ? 'error' : 'primary'}
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Tamaño: {(archivo.size / 1024).toFixed(2)} KB
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveFile(archivo.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {xmlFactura && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    ✅ XML de factura cargado exitosamente. Se integrará al XML de la adenda.
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Total de la factura: ${totalFactura.toFixed(2)}
                      </Typography>
                      {impuestosFactura.traslados > 0 && (
                        <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                          ⚠️ Impuestos (IVA) en la factura: ${impuestosFactura.traslados.toFixed(2)}
                          <br />
                          <small style={{ color: 'text.secondary' }}>
                            Asegúrate de que algún concepto en la adenda tenga este importe para evitar errores de validación.
                          </small>
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Botón para cargar Excel de Bitácoras */}
            <Card sx={{ mb: 4, backgroundColor: '#f5f5f5', border: '2px dashed #ccc' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  📊 Cargar Excel de Bitácoras (Opcional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Carga un archivo Excel con bitácoras para auto-poblar el formulario.
                  <br />
                  El Excel debe tener columnas: BITACORA, PRODUCTO, CASETAS, IVA, TOTAL
                </Typography>
                <input
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  id="upload-excel-bitacoras"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Se mantiene la compatibilidad con el uploader viejo por si acaso
                      handleExcelUpload(file, { setFieldValue, values });
                    }
                  }}
                />
                <label htmlFor="upload-excel-bitacoras">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<ExcelIcon />}
                    disabled={cargandoExcel}
                  >
                    {cargandoExcel ? 'Procesando Excel...' : 'Cargar Excel de Bitácoras'}
                  </Button>
                </label>

                {/* NUEVO INPUT Y BOTON PARA PROBAR EL EXCEL ROBUSTO */}
                <input
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  id="upload-excel-bitacoras-robusto"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleExcelImportRobusto(file, values, setFieldValue);
                      // Limpiar el input para que vuelva a disparar onChange con el mismo archivo
                      e.target.value = null;
                    }
                  }}
                />
                <label htmlFor="upload-excel-bitacoras-robusto">
                  <Button
                    variant="contained"
                    component="span"
                    color="secondary"
                    startIcon={<ExcelIcon />}
                    disabled={cargandoExcel}
                    sx={{ ml: 2 }}
                  >
                    {cargandoExcel ? 'Procesando...' : 'Prueba: Importar Nuevo Excel'}
                  </Button>
                </label>
                {cargandoExcel && (
                  <Box sx={{ mt: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Cargando y procesando bitácoras del Excel...
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <FieldArray name="conceptos">
              {({ push: pushConcepto, remove: removeConcepto, form: fieldArrayForm }) => (
                <Box>
                  {values.conceptos.map((concepto, conceptoIndex) => (
                    <Card key={conceptoIndex} sx={{ mb: 3 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6">
                            Concepto {conceptoIndex + 1}
                          </Typography>
                          {values.conceptos.length > 1 && (
                            <IconButton
                              onClick={() => removeConcepto(conceptoIndex)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={4}>
                            <Field
                              name={`conceptos.${conceptoIndex}.clave`}
                            >
                              {({ field, form }) => (
                                <ConceptoAutocomplete
                                  name={field.name}
                                  label="Clave del Concepto"
                                  value={field.value}
                                  onChange={(name, value) => {
                                    console.log('Concepto seleccionado:', value);
                                    form.setFieldValue(name, value);
                                    // Limpiar bitácoras y montos del concepto si cambia la clave 
                                    // SÓLO si previamente hemos importado un excel (tiene datos)
                                    if (excelSheetsData && excelSheetsData.length > 0) {
                                      form.setFieldValue(`conceptos.${conceptoIndex}.bitacorasViaje`, []);
                                      form.setFieldValue(`conceptos.${conceptoIndex}.importe`, 0);
                                    }
                                  }}
                                  onBlur={field.onBlur}
                                  error={form.errors.conceptos?.[conceptoIndex]?.clave}
                                  touched={form.touched.conceptos?.[conceptoIndex]?.clave}
                                  externalSearchTerm={conceptoIndex === 0 ? conceptoSearchTerm : ""}
                                  fullWidth
                                  size="small"
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={2}>
                            <FormikTextField
                              name={`conceptos.${conceptoIndex}.importe`}
                              label="Importe"
                              type="number"
                              sx={{
                                marginTop: '0px',
                                minWidth: '200px',
                                minHeight: '56px',
                              }}
                              fullWidth
                              size="small"
                              ref={conceptoImporteRef}
                            />
                            <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                              Suma bitácoras: {calcularSumaBitacoras(concepto.bitacorasViaje || []).toFixed(2)}
                              {!validarSumaBitacoras(concepto.bitacorasViaje || [], concepto.importe) && (
                                <Box sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                  ⚠️ Las sumas no coinciden
                                </Box>
                              )}
                              {calcularSumaBitacoras(concepto.bitacorasViaje || []) === 0 && (
                                <Box sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                                  ⚠️ No hay bitácoras con importe
                                </Box>
                              )}
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Descripción del Concepto"
                              value={concepto.clave?.DescripcionConcepto || concepto.clave?.Descripcion || concepto.clave?.descripcion || ''}
                              fullWidth
                              disabled
                              variant="outlined"
                              size="small"
                              multiline
                              maxRows={5}
                              sx={{
                                minWidth: '400px',
                                minHeight: '56px',
                                '& .MuiInputBase-input': {
                                  color: 'text.secondary',
                                  fontStyle: 'italic',
                                  fontSize: '0.875rem'
                                },
                                '& .MuiInputLabel-root': {
                                  color: 'text.secondary',
                                  fontSize: '0.875rem'
                                },
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'grey.50',
                                  alignItems: 'flex-start'
                                }
                              }}
                            />
                          </Grid>
                        </Grid>

                        {/* Debug: Mostrar objeto del concepto */}
                        {concepto.clave && (
                          <Box sx={{ fontSize: '0.75rem', color: 'text.disabled', mb: 1, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
                            <strong>Debug - Concepto seleccionado:</strong>
                            <pre>{JSON.stringify(concepto.clave, null, 2)}</pre>
                          </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <FieldArray name={`conceptos.${conceptoIndex}.bitacorasViaje`}>
                          {({ push: pushBitacora, remove: removeBitacora }) => (
                            <Box>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle1">
                                  Bitácoras de Viaje
                                </Typography>
                                <Button
                                  startIcon={<AddIcon />}
                                  onClick={() => {
                                    const nuevaBitacora = {
                                      claveBitacora: null,
                                      fecha: new Date().toISOString().split('T')[0],
                                      importe: 0,
                                      detalles: [{
                                        descripcion: "",
                                        fecha: new Date().toISOString().split('T')[0],
                                        importe: 0
                                      }]
                                    };
                                    pushBitacora(nuevaBitacora);
                                    // Forzar revalidación después de agregar
                                    setTimeout(() => fieldArrayForm.validateForm(), 100);
                                  }}
                                  size="small"
                                >
                                  Agregar Bitácora
                                </Button>
                              </Box>

                              {concepto.bitacorasViaje.map((bitacora, bitacoraIndex) => (
                                <Card key={bitacoraIndex} variant="outlined" sx={{ mb: 2 }}>
                                  <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                      <Typography variant="subtitle2">
                                        Bitácora {bitacoraIndex + 1}
                                      </Typography>
                                      {concepto.bitacorasViaje.length > 1 && (
                                        <IconButton
                                          onClick={() => removeBitacora(bitacoraIndex)}
                                          color="error"
                                          size="small"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      )}
                                    </Box>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                      <Grid item xs={4} marginTop={2}>
                                        <Field
                                          name={`conceptos.${conceptoIndex}.bitacorasViaje.${bitacoraIndex}.claveBitacora`}
                                        >
                                          {({ field, form }) => {
                                            console.log('Bitácora seleccionada:', field.value);
                                            return (
                                              <BitacoraAutocomplete
                                                name={field.name}
                                                label="Clave Bitácora"
                                                value={field.value?.claveBitacora || field.value}
                                                onChange={(name, value) => {
                                                  console.log('Bitácora seleccionada:', value);
                                                  form.setFieldValue(name, value);
                                                }}
                                                onBlur={field.onBlur}
                                                error={form.errors.conceptos?.[conceptoIndex]?.bitacorasViaje?.[bitacoraIndex]?.claveBitacora}
                                                touched={form.touched.conceptos?.[conceptoIndex]?.bitacorasViaje?.[bitacoraIndex]?.claveBitacora}
                                                fullWidth
                                                size="small"
                                              />
                                            )
                                          }}
                                        </Field>
                                      </Grid>
                                      <Grid item xs={4}>
                                        <FormikTextField
                                          name={`conceptos.${conceptoIndex}.bitacorasViaje.${bitacoraIndex}.fecha`}
                                          label="Fecha"
                                          type="date"
                                          fullWidth
                                        />
                                      </Grid>
                                      <Grid item xs={4}>
                                        <FormikTextField
                                          name={`conceptos.${conceptoIndex}.bitacorasViaje.${bitacoraIndex}.importe`}
                                          label="Importe"
                                          type="number"
                                          fullWidth
                                        />
                                        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                          Suma detalles: {calcularSumaDetalles(bitacora.detalles || []).toFixed(2)}
                                          {!validarSumaDetalles(bitacora.detalles || [], bitacora.importe) && (
                                            <Box sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                              ⚠️ Las sumas no coinciden
                                            </Box>
                                          )}
                                          {calcularSumaDetalles(bitacora.detalles || []) === 0 && (
                                            <Box sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                                              ⚠️ No hay detalles con importe
                                            </Box>
                                          )}
                                        </Box>
                                      </Grid>
                                    </Grid>

                                    <FieldArray name={`conceptos.${conceptoIndex}.bitacorasViaje.${bitacoraIndex}.detalles`}>
                                      {({ push: pushDetalle, remove: removeDetalle }) => (
                                        <Box>
                                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="body2" color="text.secondary">
                                              Detalles
                                            </Typography>
                                            <Button
                                              startIcon={<AddIcon />}
                                              onClick={() => {
                                                const nuevoDetalle = {
                                                  descripcion: "",
                                                  fecha: new Date().toISOString().split('T')[0],
                                                  importe: 0
                                                };
                                                pushDetalle(nuevoDetalle);
                                                // Forzar revalidación después de agregar
                                                setTimeout(() => fieldArrayForm.validateForm(), 100);
                                              }}
                                              size="small"
                                            >
                                              Agregar Detalle
                                            </Button>
                                          </Box>

                                          {bitacora.detalles.map((detalle, detalleIndex) => (
                                            <Grid container spacing={2} key={detalleIndex} sx={{ mb: 1 }}>
                                              <Grid item xs={4}>
                                                <FormikTextField
                                                  name={`conceptos.${conceptoIndex}.bitacorasViaje.${bitacoraIndex}.detalles.${detalleIndex}.descripcion`}
                                                  label="Descripción"
                                                  fullWidth
                                                />
                                              </Grid>
                                              <Grid item xs={3}>
                                                <FormikTextField
                                                  name={`conceptos.${conceptoIndex}.bitacorasViaje.${bitacoraIndex}.detalles.${detalleIndex}.fecha`}
                                                  label="Fecha"
                                                  type="date"
                                                  fullWidth
                                                />
                                              </Grid>
                                              <Grid item xs={3}>
                                                <FormikTextField
                                                  name={`conceptos.${conceptoIndex}.bitacorasViaje.${bitacoraIndex}.detalles.${detalleIndex}.importe`}
                                                  label="Importe"
                                                  type="number"
                                                  fullWidth
                                                />
                                              </Grid>
                                              <Grid item xs={2} marginTop={3}>
                                                <IconButton
                                                  onClick={() => removeDetalle(detalleIndex)}
                                                  color="error"
                                                  size="small"
                                                >
                                                  <DeleteIcon />
                                                </IconButton>
                                              </Grid>
                                            </Grid>
                                          ))}
                                        </Box>
                                      )}
                                    </FieldArray>
                                  </CardContent>
                                </Card>
                              ))}
                            </Box>
                          )}
                        </FieldArray>
                      </CardContent>
                    </Card>
                  ))}

                  <Box display="none" sx={{ mb: 2 }}>
                    {/* Botón oculto: la adenda usa un solo concepto con varias bitácoras */}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const nuevoConcepto = {
                          clave: null,
                          importe: 0,
                          bitacorasViaje: [{
                            claveBitacora: bitacoraData.claveBitacora ? {
                              claveBitacora: bitacoraData.claveBitacora,
                              serieBitacora: bitacoraData.serieBitacora || "",
                              folioBitacora: bitacoraData.folioBitacora || "",
                              origenNombre: bitacoraData.origenNombre || "",
                              destinoNombre: bitacoraData.destinoNombre || "",
                              operadorNombre: bitacoraData.operadorNombre || ""
                            } : null,
                            fecha: new Date().toISOString().split('T')[0],
                            importe: 0,
                            detalles: [{
                              descripcion: "",
                              fecha: new Date().toISOString().split('T')[0],
                              importe: 0
                            }]
                          }]
                        };
                        pushConcepto(nuevoConcepto);
                        setTimeout(() => fieldArrayForm.validateForm(), 100);
                      }}
                      variant="outlined"
                    >
                      Agregar Concepto
                    </Button>
                  </Box>
                </Box>
              )}
            </FieldArray>

            {/* Resumen de validación */}
            <Card sx={{ mt: 3, mb: 3, p: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>
                📊 Resumen de Validación
              </Typography>

              {/* Validación del subtotal de la factura */}
              {subtotalFactura > 0 && (
                <Box sx={{ mb: 3, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    💰 SubTotal de la Factura
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" color="primary.main">
                      ${subtotalFactura.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      vs Suma de conceptos: ${values.conceptos.reduce((suma, concepto) => suma + (parseFloat(concepto.importe) || 0), 0).toFixed(2)}
                    </Typography>
                    {validarSumaConceptos(values.conceptos, subtotalFactura) ? (
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                        ✅ Coincide
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                        ❌ No coincide
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Validación de impuestos */}
              {xmlFactura && impuestosFactura.traslados > 0 && (
                <Box sx={{ mb: 3, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    💳 Validación de Impuestos (IVA)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>Impuesto en la factura:</strong> ${impuestosFactura.traslados.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Importes en la adenda:</strong> {values.conceptos.map((c, i) => `Concepto ${i + 1}: $${(parseFloat(c.importe) || 0).toFixed(2)}`).join(', ')}
                    </Typography>
                    {values.conceptos.some(concepto => {
                      const importeConcepto = parseFloat(concepto.importe) || 0;
                      return Math.abs(importeConcepto - impuestosFactura.traslados) < 0.01;
                    }) ? (
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                        ✅ Hay un concepto que coincide con el importe del impuesto
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                        ❌ Ningún concepto coincide con el importe del impuesto (${impuestosFactura.traslados.toFixed(2)})
                        <br />
                        <small style={{ color: 'text.secondary' }}>
                          El backend requiere que algún concepto en la adenda tenga el mismo importe que el impuesto de la factura.
                        </small>
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {values.conceptos.map((concepto, conceptoIndex) => {
                const sumaBitacoras = calcularSumaBitacoras(concepto.bitacorasViaje || []);
                const esValidoConcepto = validarSumaBitacoras(concepto.bitacorasViaje || [], concepto.importe);

                return (
                  <Box key={conceptoIndex} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      Concepto {conceptoIndex + 1}: {concepto.clave?.CodigoGasto || 'Sin clave'}
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="body2" color={esValidoConcepto ? 'success.main' : 'error.main'}>
                        {esValidoConcepto ? '✅' : '❌'} Importe: {concepto.importe} | Suma bitácoras: {sumaBitacoras.toFixed(2)}
                      </Typography>

                      {concepto.bitacorasViaje.map((bitacora, bitacoraIndex) => {
                        const sumaDetalles = calcularSumaDetalles(bitacora.detalles || []);
                        const esValidoBitacora = validarSumaDetalles(bitacora.detalles || [], bitacora.importe);

                        return (
                          <Box key={bitacoraIndex} sx={{ ml: 2, mt: 1 }}>
                            <Typography variant="body2" color={esValidoBitacora ? 'success.main' : 'error.main'}>
                              {esValidoBitacora ? '✅' : '❌'} Bitácora {bitacoraIndex + 1}: {bitacora.importe} | Suma detalles: {sumaDetalles.toFixed(2)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Card>

            <Box display="flex" gap={2} sx={{ mt: 4, mb: 4 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => navigate('/bitacoras')}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(values)}
              >
                Descargar XML
              </Button>
              <Button
                type="button"
                variant="outlined"
                onClick={() => handlePreview(values)}
              >
                Vista Previa XML
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={loading}
                onClick={() => handlePreview(values)}
              >
                Crear Adenda
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      {/* Dialogo Multiseleccionador de Apoyo */}
      <ApoyoSelectorDialog
        open={showApoyoSelector}
        onClose={() => setShowApoyoSelector(false)}
        apoyosDisponibles={apoyosDisponibles}
        bitacorasPendientesPorApoyo={bitacorasPendientesPorApoyo}
        onSelectApoyo={continuarImportacionConBitacoras}
      />

      {/* Dialog para vista previa del XML */}
      <Dialog
        open={previewDialog}
        onClose={() => {
          setPreviewDialog(false);
          setPreviewValues(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {xmlFactura ? 'Vista Previa del XML de Factura con Adenda' : 'Vista Previa del XML de Adenda'}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowFormatted(!showFormatted)}
              sx={{ ml: 2 }}
            >
              {showFormatted ? 'Ver Sin Formato' : 'Ver Formateado'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 600,
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              whiteSpace: showFormatted ? 'pre' : 'pre-wrap',
              wordBreak: showFormatted ? 'normal' : 'break-word',
              border: '1px solid #e0e0e0',
              lineHeight: 1.3,
              tabSize: 2
            }}
          >
            {xmlPreview && xmlPreview.trim() ? (showFormatted ? formatXML(xmlPreview) : xmlPreview) : 'No hay XML para mostrar'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              if (!xmlPreview || xmlPreview.trim() === '') {
                setError('No hay XML para descargar');
                return;
              }
              const formattedXML = formatXML(xmlPreview);
              const blob = new Blob([formattedXML], { type: 'application/xml' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              const fileName = xmlFactura
                ? `factura_con_adenda_${bitacoraData.claveBitacora || 'nueva'}_${new Date().toISOString().split('T')[0]}.xml`
                : `adenda_bitacora_${bitacoraData.claveBitacora || 'nueva'}_${new Date().toISOString().split('T')[0]}.xml`;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }}
          >
            {xmlFactura ? 'Descargar Factura con Adenda' : 'Descargar Adenda'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || !previewValues}
            onClick={async () => {
              if (previewValues) {
                const valuesToSubmit = previewValues;
                setPreviewDialog(false);
                setPreviewValues(null);
                await handleSubmit(valuesToSubmit);
              }
            }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          >
            {loading ? "Enviando Adenda..." : "Enviar Adenda"}
          </Button>
          <Button onClick={() => {
            setPreviewDialog(false);
            setPreviewValues(null);
          }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ExcelBlockSelectorDialog
        open={showBlockSelector}
        onClose={() => setShowBlockSelector(false)}
        excelSheetsData={excelSheetsData}
        selectedSheetName={selectedSheetName}
        setSelectedSheetName={setSelectedSheetName}
        excelBloques={excelBloques}
        excelRawData={excelRawData}
        onProcesarBloque={procesarBloqueSeleccionado}
      />

      {/* Dialog para éxito */}
      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40 }} />
            <Typography variant="h6">
              Adenda Enviada con Éxito
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            La adenda ha sido creada y enviada exitosamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSuccessDialog(false);
              navigate('/bitacoras');
            }}
          >
            Ir a Bitácoras
          </Button>
          <Button
            onClick={() => setSuccessDialog(false)}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para error */}
      <Dialog
        open={errorDialog}
        onClose={() => setErrorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 40 }} />
            <Typography variant="h6">
              Error al Enviar Adenda
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
            {errorMessage || "Ocurrió un error al intentar crear la adenda. Por favor, intente nuevamente."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setErrorDialog(false)}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}